import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Factory, ShieldCheck, Handshake, Ship, Zap, Mountain, Activity, Hammer, Box, Globe2 } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

const iconMap: Record<string, any> = { Factory, ShieldCheck, Handshake, Ship, Zap, Mountain, Activity, Hammer, Box, Globe2 };

const MiningOperations: React.FC<{ content?: any }> = ({ content }) => {
  const [active, setActive] = useState<number>(0);
  const { t, t_api } = useLanguage();

  const panels = useMemo(
    () => {
      if (content?.sections && content.sections.length > 0) {
        return content.sections.map((s: any, idx: number) => ({
          id: idx,
          title: s.title,
          icon: iconMap[s.iconName] || Factory,
          desc: s.desc
        }));
      }

      // Default/Fallback structure
      return [
        {
          id: 0,
          title: content?.prod?.title || t_api("Production Capacity & Operations", "الطاقة الإنتاجية والعمليات"),
          icon: Factory,
          desc: content?.prod?.desc || "The Liwa Quarry features a high-capacity crushing plant...",
        },
        {
          id: 1,
          title: content?.hse?.title || t_api("Commitment to HSE & Quality", "الالتزام بالجودة والصحة والسلامة والبيئة"),
          icon: ShieldCheck,
          desc: content?.hse?.desc || "Hamdan Mining International maintains rigorous health, safety, and environment standards...",
        },
        {
          id: 2,
          title: content?.customer?.title || t_api("Customer Focus & Service Reliability", "التركيز على العملاء وموثوقية الخدمة"),
          icon: Handshake,
          desc: content?.customer?.desc || "We prioritize long-term partnerships through consistent quality and timely delivery...",
        },
        {
          id: 3,
          title: content?.export?.title || t_api("Export & Market Coverage", "التصدير وتغطية الأسواق"),
          icon: Ship,
          desc: content?.export?.desc || "Our products are exported across the GCC and international markets...",
        },
      ];
    },
    [content, t_api]
  );

  const activePanel = panels[active] || panels[0];

  return (
    <section className="bg-white py-10">
      <div className="max-w-7xl mx-auto px-4 space-y-8">
        <div className="rounded-3xl border border-gray-200 bg-gray-50 p-8 shadow-sm">
          <div className="text-xs uppercase tracking-widest text-gray-500">
            {t_api("Hamdan Mining", "حمدان للتعدين")}
          </div>
          <h2 className="mt-2 text-3xl font-bold text-gray-900">
            {t('common.operations_overview')}
          </h2>
          <p className="mt-3 max-w-3xl text-sm text-gray-600 leading-relaxed">
            {t_api("Production capacity, HSE & quality, customer reliability, and export market coverage.", "الطاقة الإنتاجية، الجودة والسلامة، موثوقية العملاء، وتغطية أسواق التصدير.")}
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* LEFT MENU */}
          <div className="lg:col-span-4">
            <div className="rounded-2xl border bg-gray-50 p-3">
              {panels.map((p: any, idx: number) => {
                const Icon = p.icon;
                const isActive = idx === active;

                return (
                  <button
                    key={idx}
                    onClick={() => setActive(idx)}
                    className={`w-full text-left rounded-2xl p-4 mb-3 last:mb-0 border transition-all
                      ${isActive
                        ? "bg-yellow-100 border-yellow-300 shadow-sm"
                        : "bg-transparent border-transparent hover:bg-yellow-50 hover:border-yellow-200"
                      }`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`w-11 h-11 rounded-xl flex items-center justify-center
                          ${isActive
                            ? "bg-gradient-to-br from-orange-400 to-orange-700 text-white"
                            : "bg-white border border-yellow-300 text-red-700"
                          }`}
                      >
                        <Icon className="w-5 h-5" />
                      </span>

                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 text-sm md:text-base leading-tight">
                          {p.title}
                        </div>
                        <div className="text-[10px] uppercase font-bold text-gray-400 mt-1 tracking-wider">
                          {t('common.click_to_view')}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* RIGHT CONTENT */}
          <div className="lg:col-span-8">
            <div className="rounded-3xl border bg-white shadow-sm overflow-hidden h-full flex flex-col">
              <div className="px-6 md:px-8 py-6 border-b bg-[#fffaf0]">
                <div className="text-[10px] uppercase tracking-widest text-red-600 font-bold">
                  {t('common.selected_section')}
                </div>
                <div className="mt-1 text-xl md:text-2xl font-bold text-gray-900 font-cambria">
                  {activePanel.title}
                </div>
              </div>
              <div className="p-6 md:p-8 text-sm md:text-base flex-1">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activePanel.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.25 }}
                    className="space-y-4 text-gray-700 leading-relaxed text-justify"
                  >
                    <div dangerouslySetInnerHTML={{ __html: (activePanel.desc || "").replace(/\n/g, '<br/>') }} />
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MiningOperations;
